/**
 * Sidebar Module
 * Handles dynamic sidebar scroll positioning
 *
 * Behavior:
 * - Scrolls normally until sidebar top hits viewport top
 * - When scrolling DOWN: aligns sidebar bottom to viewport bottom (if sidebar > viewport)
 * - When scrolling UP: aligns sidebar top to viewport top (under header)
 * - Ensures entire sidebar is accessible even when taller than viewport
 */

import { getHeaderHeight } from './header.js';

let sidebar = null;
let sidebarInner = null;
let mainColumn = null;
let initialOffset = 0;
let lastScrollTop = 0;
let targetMarginTop = 0;
let renderedMarginTop = 0;
let animationFrameId = null;
let lastAnimationTime = 0;

const TOP_GAP = 35;
const BOTTOM_GAP = 30;
const FOLLOW_TIME = 140;
const SNAP_THRESHOLD = 0.5;

export function initSidebar() {

    sidebar = document.querySelector('.office-sidebar');
    sidebarInner = document.getElementById('sidebar-inner');
    mainColumn = document.querySelector('.content-column');

    if (!sidebar || !sidebarInner || !mainColumn) {
        return;
    }

    recalculateOffset();

    // Use ResizeObserver to detect height changes (e.g. amenities expanding)
    const ro = new ResizeObserver(() => {
        recalculateOffset();
        updateSidebarPosition({ immediate: true });
    });
    ro.observe(mainColumn);
    ro.observe(sidebarInner);

    // Initial calculation after load
    window.addEventListener('load', () => {
        recalculateOffset();
        updateSidebarPosition({ immediate: true });
    });

    // Handle scroll by updating the target position, then animate toward it.
    let ticking = false;
    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                updateSidebarPosition();
                ticking = false;
            });
            ticking = true;
        }
    }, { passive: true });

    // Handle resize
    window.addEventListener('resize', () => {
        recalculateOffset();
        updateSidebarPosition({ immediate: true });
    });
}

function recalculateOffset() {
    initialOffset = sidebar.getBoundingClientRect().top + window.scrollY;
}

function updateSidebarPosition({ immediate = false } = {}) {
    if (initialOffset == null || !sidebarInner) return;

    targetMarginTop = calculateTargetMarginTop();

    if (immediate) {
        renderedMarginTop = targetMarginTop;
        applySidebarMargin(renderedMarginTop);
        stopSidebarAnimation();
        return;
    }

    startSidebarAnimation();
}

function calculateTargetMarginTop() {
    // Only apply when sidebar is beside the content (multi-column layout)
    const layoutContainer = sidebar.closest('.listing-layout, .content-grid');
    if (layoutContainer) {
        const cols = getComputedStyle(layoutContainer).gridTemplateColumns.trim().split(/\s+/);
        if (cols.length < 2) {
            lastScrollTop = window.scrollY;
            return 0;
        }
    }

    const scrollTop = window.scrollY;
    const headerHeight = getHeaderHeight();
    const viewportHeight = window.innerHeight;
    const sidebarHeight = sidebarInner.offsetHeight;
    const mainColumnHeight = getMainColumnHeight();

    // Determine scroll direction
    const isScrollingDown = scrollTop > lastScrollTop;
    lastScrollTop = scrollTop;

    // Only apply if main content is taller than sidebar
    if (mainColumnHeight <= sidebarHeight) {
        return 0;
    }

    // Viewport boundaries relative to the page
    const viewportTop = scrollTop + headerHeight + TOP_GAP;
    const viewportBottom = scrollTop + viewportHeight - BOTTOM_GAP;

    // Max margin to stay inside main column
    const maxMargin = mainColumnHeight - sidebarHeight;

    // Check if sidebar fits in the available viewport space
    const availableViewportSpace = viewportHeight - headerHeight - TOP_GAP - BOTTOM_GAP;
    let nextTargetMargin = targetMarginTop;

    if (sidebarHeight <= availableViewportSpace) {
        nextTargetMargin = viewportTop - initialOffset;
    } else {
        if (isScrollingDown) {
            const bottomTargetMargin = viewportBottom - initialOffset - sidebarHeight;
            if (nextTargetMargin < bottomTargetMargin) {
                nextTargetMargin = bottomTargetMargin;
            }
        } else {
            const topTargetMargin = viewportTop - initialOffset;
            if (nextTargetMargin > topTargetMargin) {
                nextTargetMargin = topTargetMargin;
            }
        }
    }

    // Constrain to container boundaries
    if (nextTargetMargin < 0) nextTargetMargin = 0;
    if (nextTargetMargin > maxMargin) nextTargetMargin = maxMargin;

    return nextTargetMargin;
}

function startSidebarAnimation() {
    if (animationFrameId !== null) return;
    animationFrameId = window.requestAnimationFrame(stepSidebarAnimation);
}

function stepSidebarAnimation(timestamp) {
    if (lastAnimationTime === 0) {
        lastAnimationTime = timestamp;
    }

    const delta = timestamp - lastAnimationTime;
    lastAnimationTime = timestamp;

    const easing = 1 - Math.exp(-delta / FOLLOW_TIME);
    renderedMarginTop += (targetMarginTop - renderedMarginTop) * easing;

    if (Math.abs(targetMarginTop - renderedMarginTop) <= SNAP_THRESHOLD) {
        renderedMarginTop = targetMarginTop;
        applySidebarMargin(renderedMarginTop);
        stopSidebarAnimation();
        return;
    }

    applySidebarMargin(renderedMarginTop);
    animationFrameId = window.requestAnimationFrame(stepSidebarAnimation);
}

function applySidebarMargin(value) {
    sidebarInner.style.marginTop = `${value}px`;
}

function stopSidebarAnimation() {
    if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
    }
    animationFrameId = null;
    lastAnimationTime = 0;
}

function getMainColumnHeight() {
    return mainColumn ? mainColumn.offsetHeight : 0;
}
